import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { Input, Spin, Modal, message } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import MetroInfoPanel from '../MetroInfoPanel';
import DynamicMarker from '../markers/DynamicMarker';
import FormikForm from '../../form/FormikForm';
import ReactDOMServer from 'react-dom/server';
import { debounce } from 'lodash';
import { getMarkerImage } from '../markers/markerImages';
import axios from 'axios';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';
import { getImageUrls } from '../../../utils/poifotoUtils';
import { GOOGLE_MAPS_API_KEY } from '../../../utils/googleMaps';

// Google Maps dark mode style array
const darkMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
];

const lightMapStyles = [];

const createDistanceLabelOverlay = (position, text, map, darkMode = false) => {
  if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
    return null;
  }

  class DistanceLabelOverlay extends window.google.maps.OverlayView {
    constructor(pos, txt, isDark) {
      super();
      this.position = pos;
      this.text = txt;
      this.darkMode = isDark;
      this.div = null;
    }

    onAdd() {
      this.div = document.createElement('div');
      const bgColor = this.darkMode ? 'rgba(33, 33, 33, 0.95)' : 'rgba(255, 255, 255, 0.95)';
      const textColor = this.darkMode ? '#ffffff' : '#000000';
      const borderColor = this.darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)';
      const shadowColor = this.darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)';
      
      this.div.style.cssText = `
        position: absolute;
        background: ${bgColor};
        color: ${textColor};
        padding: 4px 8px;
        border-radius: 4px;
        font-weight: bold;
        font-size: 12px;
        box-shadow: 0 2px 4px ${shadowColor};
        white-space: nowrap;
        pointer-events: none;
        border: 1px solid ${borderColor};
      `;
      this.div.textContent = this.text;
      
      const panes = this.getPanes();
      if (panes && panes.overlayMouseTarget) {
        panes.overlayMouseTarget.appendChild(this.div);
      }
    }

    draw() {
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !this.div) return;
      
      const position = overlayProjection.fromLatLngToDivPixel(this.position);
      
      if (this.div) {
        this.div.style.left = (position.x - this.div.offsetWidth / 2) + 'px';
        this.div.style.top = (position.y - 10) + 'px';
      }
    }

    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
      }
    }
  }

  return new DistanceLabelOverlay(position, text, darkMode);
};

const openImageModal = (images, startIndex, maptriksId, rawLokasyonResim) => {
  const highQualityImages = getImageUrls(rawLokasyonResim, maptriksId, { isHighQuality: true, includeTitle: true });
  let currentIndex = startIndex;

  const existingModal = document.getElementById('imageModal');
  if (existingModal) {
    document.body.removeChild(existingModal);
  }

  const modalOverlay = document.createElement('div');
  modalOverlay.id = 'imageModal';
  Object.assign(modalOverlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: '10000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  });

  const imageContainer = document.createElement('div');
  Object.assign(imageContainer.style, {
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
    cursor: 'default',
    pointerEvents: 'auto' 
  });

  const mainImage = document.createElement('img');
  Object.assign(mainImage.style, {
    maxWidth: '100%',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    display: 'block',
    userSelect: 'none'
  });

  const closeButton = document.createElement('button');
  closeButton.innerHTML = '×';
  Object.assign(closeButton.style, {
    position: 'absolute',
    top: '-45px',
    right: '-10px', 
    backgroundColor: '#fff', 
    color: '#000',
    border: '2px solid #fff', 
    borderRadius: '50%',
    width: '44px', 
    height: '44px',
    cursor: 'pointer',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    zIndex: '10002', 
    lineHeight: '0.8', 
    padding: '0',
    boxShadow: '0 2px 10px rgba(0,0,0,0.5)'
  });

  let prevButton, nextButton, imageCounter;
  if (images.length > 1) {
    const navButtonStyle = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(255,255,255,0.9)',
      color: '#333',
      border: 'none',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      cursor: 'pointer',
      fontSize: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
      fontWeight: 'bold',
      zIndex: '10002',
      lineHeight: '1'
    };

    prevButton = document.createElement('button');
    prevButton.innerHTML = '‹';
    Object.assign(prevButton.style, { ...navButtonStyle, left: '-70px' });

    nextButton = document.createElement('button');
    nextButton.innerHTML = '›';
    Object.assign(nextButton.style, { ...navButtonStyle, right: '-70px' });

    imageCounter = document.createElement('div');
    Object.assign(imageCounter.style, {
      position: 'absolute',
      bottom: '-40px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(255,255,255,0.9)',
      color: '#333',
      padding: '4px 16px',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: 'bold'
    });
  }

  const updateImage = () => {
    const currentImage = highQualityImages[currentIndex] || images[currentIndex];
    mainImage.src = currentImage.url;
    mainImage.alt = currentImage.title;
    if (imageCounter) {
      imageCounter.textContent = `${currentIndex + 1} / ${images.length}`;
    }
  };

  imageContainer.appendChild(mainImage);
  imageContainer.appendChild(closeButton);
  if (images.length > 1) {
    imageContainer.appendChild(prevButton);
    imageContainer.appendChild(nextButton);
    imageContainer.appendChild(imageCounter);
  }
  modalOverlay.appendChild(imageContainer);
  document.body.appendChild(modalOverlay);

  updateImage();

  const closeModal = () => {
    if (document.body.contains(modalOverlay)) {
      document.body.removeChild(modalOverlay);
      document.removeEventListener('keydown', handleKeyDown);
    }
  };

  imageContainer.addEventListener('mousedown', (e) => {
    e.stopPropagation();
  });

  closeButton.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeModal();
  };

  if (prevButton) {
    prevButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      currentIndex = (currentIndex - 1 + images.length) % images.length;
      updateImage();
    };
  }

  if (nextButton) {
    nextButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      currentIndex = (currentIndex + 1) % images.length;
      updateImage();
    };
  }

  modalOverlay.onclick = (e) => {
    if (e.target === modalOverlay) {
      e.preventDefault();
      e.stopPropagation();
      closeModal();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeModal();
    else if (e.key === 'ArrowLeft' && images.length > 1) {
      currentIndex = (currentIndex - 1 + images.length) % images.length;
      updateImage();
    } else if (e.key === 'ArrowRight' && images.length > 1) {
      currentIndex = (currentIndex + 1) % images.length;
      updateImage();
    }
  };

  document.addEventListener('keydown', handleKeyDown);
};

const calculateCenterFromData = (data) => {
  if (!data || data.length === 0) {
    return { lat: 39.9334, lng: 32.8597 };
  }

  const validLocations = data.filter(item => 
    item.coordinates && 
    item.coordinates.lat && 
    item.coordinates.lng &&
    !isNaN(item.coordinates.lat) && 
    !isNaN(item.coordinates.lng)
  );

  if (validLocations.length === 0) {
    return { lat: 39.9334, lng: 32.8597 };
  }

  const totalLat = validLocations.reduce((sum, item) => sum + item.coordinates.lat, 0);
  const totalLng = validLocations.reduce((sum, item) => sum + item.coordinates.lng, 0);
  
  return {
    lat: totalLat / validLocations.length,
    lng: totalLng / validLocations.length
  };
};

const calculateBoundsFromData = (data) => {
  if (!data || data.length === 0) return null;

  const validLocations = data.filter(item => 
    item.coordinates && 
    item.coordinates.lat && 
    item.coordinates.lng &&
    !isNaN(item.coordinates.lat) && 
    !isNaN(item.coordinates.lng)
  );

  if (validLocations.length === 0) return null;

  const lats = validLocations.map(item => item.coordinates.lat);
  const lngs = validLocations.map(item => item.coordinates.lng);

  const bounds = {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs)
  };

  return bounds;
};

const MapContainer = forwardRef(({ data = [], darkMode, selectedItemId, onSelectLocation, hideDetailLink = false, detailLinkPath = 'detay', showBkmAtms = false, offsiteAtms = [], isMobile = false }, ref) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const markerClusterer = useRef(null);
  const infoWindowRef = useRef(null);
  const transitLayerRef = useRef(null);
  const debouncedUpdateMarkers = useCallback(debounce(() => {
    if (markerClusterer.current) {
      markerClusterer.current.repaint();
    }
  }, 300), []);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showMetroInfo, setShowMetroInfo] = useState(false);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchBoxRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(14);
  const [offsiteData, setOffsiteData] = useState([]);
  // Offsite katmani: harita pan/zoom ettikce merkez cevresindeki (1km) TUM banka offsite ATM'leri cek
  useEffect(() => {
    if (!showBkmAtms || !mapLoaded || !mapInstance.current) { setOffsiteData([]); return; }
    const c = mapInstance.current.getCenter();
    if (!c) return;
    let cancelled = false;
    fetch(`${SHARED_API_BASE_URL}/atm/offsite-near?lat=${c.lat()}&lng=${c.lng()}&radius=1000`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const arr = (j.data || []).map((a, i) => ({
          id: `offsite-${a.bankId}-${a.latitude}-${a.longitude}-${i}`,
          bkm: true, banka: a.banka, onsite: a.onsite,
          coordinates: { lat: Number(a.latitude), lng: Number(a.longitude) },
          name: `${a.banka || 'Offsite ATM'} (${a.mesafe_m}m)`,
        }));
        setOffsiteData(arr);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showBkmAtms, mapBounds, mapLoaded]);
  const [filteredBkmData, setFilteredBkmData] = useState([]);
  const [filteredNormalData, setFilteredNormalData] = useState([]);
  const [isAddMarkerMode, setIsAddMarkerMode] = useState(false);
  const [customMarkers, setCustomMarkers] = useState([]);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [selectedMarkerData, setSelectedMarkerData] = useState(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [isMeasuringDistance, setIsMeasuringDistance] = useState(false);
  const [firstPoint, setFirstPoint] = useState(null);
  const [secondPoint, setSecondPoint] = useState(null);
  const distanceLinesRef = useRef([]);
  const distanceMarkersRef = useRef([]);
  const distanceLabelsRef = useRef([]);
  const isFirstPointSelectedRef = useRef(false);
  const isMeasuringDistanceRef = useRef(false);
  const isAddMarkerModeRef = useRef(false);
  const [draggingMarkerId, setDraggingMarkerId] = useState(null);
  const [newCoordinates, setNewCoordinates] = useState(null);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const originalCoordinatesRef = useRef(null);
  const isSavingLocationRef = useRef(false);
  const savedCoordinatesRef = useRef({});
  const newCoordinatesRef = useRef(null); // Drag sırasında geçici koordinatlar için ref
  const API_BASE_URL = SHARED_API_BASE_URL;

  const filterBkmAtmsByViewport = useCallback((bounds, zoom) => {
    if (!bounds) return [];
    const c = bounds.getCenter();
    const clat = c.lat();
    const clng = c.lng();
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371000;
    const bkmAtms = data.filter((item) => item.bkm);
    return bkmAtms.filter((item) => {
      const lat = item.coordinates.lat;
      const lng = item.coordinates.lng;
      const dLat = toRad(lat - clat);
      const dLng = toRad(lng - clng);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(clat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
      const dist = 2 * R * Math.asin(Math.sqrt(a));
      return dist <= 1000; // görünür merkezin 1km çevresi
    });
  }, [data]);

  const filterNormalLocationsByViewport = useCallback((bounds) => {
    if (!bounds) return [];
    
    const normalLocations = data.filter(item => !item.bkm);
    return normalLocations.filter(item => {
      const lat = item.coordinates.lat;
      const lng = item.coordinates.lng;
      
      return lat >= bounds.getSouthWest().lat() && 
             lat <= bounds.getNorthEast().lat() && 
             lng >= bounds.getSouthWest().lng() && 
             lng <= bounds.getNorthEast().lng();
    });
  }, [data]);

  const getAddressFromNominatim = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=tr`
      );
      const data = await response.json();      
      if (data && data.display_name) {
        return data.display_name;
      }
      return `Koordinat: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (error) {
      console.warn('Nominatim API hatası:', error);
      return `Koordinat: ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  const getAddressFromCoordinates = useCallback((lat, lng) => {
    return new Promise(async (resolve) => {
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        const timeout = setTimeout(() => {
          console.warn('Google Geocoding timeout, Nominatim deneniyor...');
          getAddressFromNominatim(lat, lng).then(resolve);
        }, 3000);

        geocoder.geocode({
          location: { lat, lng },
          language: 'tr'
        }, (results, status) => {
          clearTimeout(timeout);
          if (status === 'OK' && results && results.length > 0) {
            const result = results[0];
            const addressComponents = result.address_components;
            const addressDetails = {
              fullAddress: result.formatted_address,
              province: '', 
              district: '', 
              neighborhood: '', 
              postalCode: '',
              streetNumber: '', 
              route: '', 
              sublocality: ''
            };

            addressComponents.forEach(component => {
              const types = component.types;
              if (types.includes('administrative_area_level_1')) {
                addressDetails.province = component.long_name;
              } else if (types.includes('administrative_area_level_2')) {
                addressDetails.district = component.long_name; 
              } else if (types.includes('sublocality_level_1') || types.includes('neighborhood')) {
                addressDetails.neighborhood = component.long_name;
              } else if (types.includes('postal_code')) {
                addressDetails.postalCode = component.long_name;
              } else if (types.includes('street_number')) {
                addressDetails.streetNumber = component.long_name; 
              } else if (types.includes('route')) {
                addressDetails.route = component.long_name;
              } else if (types.includes('sublocality')) {
                addressDetails.sublocality = component.long_name; 
              }
            });

            resolve(addressDetails);
          } else {
            console.warn('Google Geocoding başarısız, Nominatim deneniyor...');
            getAddressFromNominatim(lat, lng).then(fallbackAddress => {
              resolve({
                fullAddress: fallbackAddress,
                province: '',
                district: '',
                neighborhood: '',
                postalCode: '',
                streetNumber: '',
                route: '',
                sublocality: ''
              });
            });
          }
        });
      } else {
        console.warn('Google Maps API yüklenmemiş, Nominatim deneniyor...');
        getAddressFromNominatim(lat, lng).then(fallbackAddress => {
          resolve({
            fullAddress: fallbackAddress,
            province: '',
            district: '',
            neighborhood: '',
            postalCode: '',
            streetNumber: '',
            route: '',
            sublocality: ''
          });
        });
      }
    });
  }, []);

  const addCustomMarker = useCallback(async (lat, lng) => {
    setIsLoadingAddress(true);
    const addressDetails = await getAddressFromCoordinates(lat, lng);
    const markerId = `custom-${Date.now()}`;

    const markerData = {
      id: markerId,
      coordinates: { lat, lng },
      address: addressDetails.fullAddress,
      addressDetails: addressDetails, 
      name: `Özel Marker ${customMarkers.length + 1}`,
      isCustom: true
    };

    setCustomMarkers(prev => [...prev, markerData]);
    setIsLoadingAddress(false);

    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance.current,
      title: `${markerData.name} - Hareket ettirmek için sürükleyin`,
      draggable: true, 
      cursor: 'move',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="#ff4d4f" stroke="#fff" stroke-width="2"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">+</text>
            <path d="M8 6h8M8 12h8M8 18h8" stroke="white" stroke-width="1" stroke-linecap="round"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16)
      }
    });

    marker.addListener('click', () => {
      setSelectedMarkerData({
        id: markerId,
        enlem: lat,
        boylam: lng,
        adres: addressDetails.fullAddress,
        adresDetaylari: addressDetails, 
        isim: markerData.name
      });
      setIsFormModalVisible(true);
    });

    marker.addListener('dragend', async (event) => {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      setIsLoadingAddress(true);
      const newAddressDetails = await getAddressFromCoordinates(newLat, newLng);

      setCustomMarkers(prev => prev.map(m =>
        m.id === markerId
          ? {
              ...m,
              coordinates: { lat: newLat, lng: newLng },
              address: newAddressDetails.fullAddress,
              addressDetails: newAddressDetails 
            }
          : m
      ));
      marker.setTitle(`${markerData.name} - Yeni konum: ${newLat.toFixed(6)}, ${newLng.toFixed(6)}`);
      setIsLoadingAddress(false);
    });

    marker.addListener('dragstart', () => {
      marker.setTitle('Marker hareket ettiriliyor...');
    });

    markersRef.current[markerId] = marker;
  }, [customMarkers.length, getAddressFromCoordinates]);

  const clearDistanceMeasurement = useCallback(() => {
    distanceLinesRef.current.forEach(line => {
      if (line) {
        line.setMap(null);
      }
    });
    distanceLinesRef.current = [];
    
    distanceLabelsRef.current.forEach(label => {
      if (label) {
        label.setMap(null);
      }
    });
    distanceLabelsRef.current = [];
    
    distanceMarkersRef.current.forEach(marker => {
      if (marker) {
        marker.setMap(null);
      }
    });
    distanceMarkersRef.current = [];
    
    setFirstPoint(null);
    setSecondPoint(null);
    setIsMeasuringDistance(false);
    isMeasuringDistanceRef.current = false;
    isFirstPointSelectedRef.current = false;
  }, []);

  const updateDistanceLines = useCallback(() => {
    if (!mapInstance.current || !window.google || !window.google.maps) return;
    if (!window.google.maps.geometry || !window.google.maps.geometry.spherical) return;

    distanceLinesRef.current.forEach(line => {
      if (line) line.setMap(null);
    });
    distanceLinesRef.current = [];
    
    distanceLabelsRef.current.forEach(label => {
      if (label) label.setMap(null);
    });
    distanceLabelsRef.current = [];

    const lineColor = darkMode ? '#ffffff' : '#000000';
    const lineOpacity = darkMode ? 0.9 : 1.0;

    for (let i = 0; i < distanceMarkersRef.current.length - 1; i++) {
      const marker1 = distanceMarkersRef.current[i];
      const marker2 = distanceMarkersRef.current[i + 1];
      
      if (!marker1 || !marker2) continue;

      const pos1 = marker1.getPosition();
      const pos2 = marker2.getPosition();
      
      const point1 = new window.google.maps.LatLng(pos1.lat(), pos1.lng());
      const point2 = new window.google.maps.LatLng(pos2.lat(), pos2.lng());

      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(point1, point2);
      const distanceInMeters = Math.round(distance * 100) / 100;

      let formattedDistance;
      if (distanceInMeters >= 1000) {
        const distanceInKm = distanceInMeters / 1000;
        formattedDistance = `${distanceInKm.toFixed(2).replace('.', ',')} km`;
      } else {
        formattedDistance = `${distanceInMeters.toFixed(2).replace('.', ',')} m`;
      }

      const line = new window.google.maps.Polyline({
        path: [point1, point2],
        geodesic: true,
        strokeColor: lineColor,
        strokeOpacity: lineOpacity,
        strokeWeight: 3,
        map: mapInstance.current,
        zIndex: 999,
        clickable: true
      });
      
      line.segmentIndex = i; 
      line.addListener('click', (event) => {
        event.stop(); 
        if (!isMeasuringDistanceRef.current || !isFirstPointSelectedRef.current) return;
        
        const clickLat = event.latLng.lat();
        const clickLng = event.latLng.lng();
        
        const segmentIdx = line.segmentIndex;
        if (segmentIdx !== undefined) {
          addDistancePoint(clickLat, clickLng, segmentIdx + 1);
        }
      });
      
      distanceLinesRef.current.push(line);

      const midLat = (pos1.lat() + pos2.lat()) / 2;
      const midLng = (pos1.lng() + pos2.lng()) / 2;
      const midPoint = new window.google.maps.LatLng(midLat, midLng);

      const label = createDistanceLabelOverlay(midPoint, formattedDistance, mapInstance.current, darkMode);
      if (label) {
        label.setMap(mapInstance.current);
        distanceLabelsRef.current.push(label);
      }
    }
  }, [darkMode]);

  const addDistancePoint = useCallback((lat, lng, index = null) => {
    if (!mapInstance.current || !window.google || !window.google.maps) return;

    const markerFill = darkMode ? '#ffffff' : '#000000';
    const markerStroke = darkMode ? '#000000' : '#ffffff';
    const innerFill = darkMode ? '#000000' : '#ffffff';

    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance.current,
      draggable: true,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="${markerFill}" stroke="${markerStroke}" stroke-width="2"/>
            <circle cx="12" cy="12" r="4" fill="${innerFill}"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(24, 24),
        anchor: new window.google.maps.Point(12, 12)
      },
      zIndex: 1000
    });

    marker.addListener('dragend', () => {
      updateDistanceLines();
    });

    marker.index = index !== null ? index : distanceMarkersRef.current.length;
    
    if (index !== null) {
      distanceMarkersRef.current.splice(index, 0, marker);
      for (let i = index + 1; i < distanceMarkersRef.current.length; i++) {
        distanceMarkersRef.current[i].index = i;
      }
    } else {
      distanceMarkersRef.current.push(marker);
    }

    updateDistanceLines();
  }, [updateDistanceLines, darkMode]);

  const measureDistance = useCallback((lat, lng, isRightClick = false) => {
    if (!mapInstance.current || !window.google || !window.google.maps) return;

    if (!window.google.maps.geometry || !window.google.maps.geometry.spherical) {
      console.error('Google Maps Geometry library yüklenmemiş!');
      return;
    }

    if (isRightClick && !isFirstPointSelectedRef.current) {
      clearDistanceMeasurement();
      setFirstPoint({ lat, lng });
      setIsMeasuringDistance(true);
      isMeasuringDistanceRef.current = true;
      setIsAddMarkerMode(false);
      isAddMarkerModeRef.current = false;
      isFirstPointSelectedRef.current = true;
      addDistancePoint(lat, lng);
      return;
    }

    if (!isRightClick && isFirstPointSelectedRef.current) {
      addDistancePoint(lat, lng);
    }
  }, [clearDistanceMeasurement, addDistancePoint]);

  const showInfoWindow = useCallback((marker, item, newCoords = null) => {
      if (!mapInstance.current || !marker) return;
      
      // Eğer aynı marker için infoWindow zaten açıksa, sadece içeriği güncelle
      const isSameMarker = infoWindowRef.current && 
                          infoWindowRef.current.getAnchor && 
                          infoWindowRef.current.getAnchor() === marker;
      
      if (!infoWindowRef.current) {
        if (!window.google || !window.google.maps) return;
        infoWindowRef.current = new window.google.maps.InfoWindow();
      }
      
      // Eğer farklı bir marker için açıksa, önce kapat
      if (!isSameMarker && infoWindowRef.current.getAnchor) {
        infoWindowRef.current.close();
      }
  
      let images = [];
      const maptriksIdForImages = item.maptriksID || item.id;
      if (item.bkm && item.banka) {
        images = getImageUrls(item.lokasyonResim, maptriksIdForImages, { includeTitle: true });
      } else {
        images = getImageUrls(item.lokasyonResim, item.maptriksID, { includeTitle: true });
      }
  
      const createImageCarousel = (images) => {
        if (!images || images.length === 0) {
          return `
            <div style="height: 150px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; margin-bottom: 12px;">
              📍
            </div>
          `;
        }
        return `
          <div style="position: relative; margin-bottom: 12px;">
            <div id="imageContainer" style="height: 150px; border-radius: 8px; overflow: hidden; position: relative; background: #f0f2f5; cursor: pointer;">
              <img id="currentImage" src="${images[0].url}" alt="${images[0].title}" 
                   style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px;" />
              ${images.length > 1 ? `
                <div style="position: absolute; top: 8px; right: 8px; background: rgba(0,0,0,0.5); color: white; padding: 4px 8px; border-radius: 12px; font-size: 12px; pointer-events: none;">
                  <span id="imageCounter">1 / ${images.length}</span>
                </div>
              ` : ''}
              <div style="position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.7); color: white; padding: 2px 8px; border-radius: 8px; font-size: 11px; pointer-events: none;">
                🔍 Büyütmek için tıklayın
              </div>
            </div>
          </div>
        `;
      };
      
      const markerIcon = item.bkm && item.banka ?
        ReactDOMServer.renderToString(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px' }}>
            <img src={getMarkerImage(item.banka)} alt={item.banka} style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
          </div>
        ) :
        ReactDOMServer.renderToString(
          <DynamicMarker
            firma={item.firma}  
            durum={item.durum}
            durum_name={item.durum_name}
            resim={item.lokasyonResim}
            size={24}
          />
        );
  
      infoWindowRef.current.setContent(`
        <div style="padding: 12px; min-width: 250px; max-width: 300px;">
          ${createImageCarousel(images)}
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            ${markerIcon}
            <h3 style="margin: 0 0 0 8px; color: ${darkMode ? '#1890ff' : '#1890ff'};">${item.name}</h3>
          </div>
          <p style="margin: 4px 0; color: ${darkMode ? '#261717' : '#666'};"><strong>ID:</strong> ${item.maptriksID || item.id}</p>
          <p style="margin: 4px 0; color: ${darkMode ? '#261717' : '#666'};"><strong>Adres:</strong> ${item.address}</p>
          ${item.bkm && item.banka ? `
            <p style="margin: 4px 0; color: ${darkMode ? '#261717' : '#666'};"><strong>Banka:</strong> ${item.banka}</p>
            ${item.onsite ? `<p style="margin: 4px 0; color: ${darkMode ? '#261717' : '#666'};"><strong>Onsite:</strong> ${item.onsite}</p>` : ''}
            ${item.offsite ? `<p style="margin: 4px 0; color: ${darkMode ? '#261717' : '#666'};"><strong>Offsite:</strong> ${item.offsite}</p>` : ''}
          ` : ''}
          <div style="margin-top: 8px; display: flex; gap: 8px; flex-direction: column;">
            ${draggingMarkerId === (item.maptriksID || item.id) ? `
              <div style="display: flex; gap: 8px;">
                <button id="saveLocationBtn" 
                        style="flex: 1; display: flex; align-items: center; justify-content: center; background: ${darkMode ? '#52c41a' : '#52c41a'}; color: #FFFFFF; padding: 6px 12px; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 2px 0 rgba(0, 0, 0, 0.043);">
                  ${isSavingLocation ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
                <button id="cancelDraggingBtn" 
                        style="flex: 1; display: flex; align-items: center; justify-content: center; background: ${darkMode ? '#ff4d4f' : '#ff4d4f'}; color: #FFFFFF; padding: 6px 12px; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 2px 0 rgba(0, 0, 0, 0.043);">
                  İptal
                </button>
              </div>
              ${(newCoords || newCoordinates || newCoordinatesRef.current) ? (() => {
                const coords = newCoords || newCoordinates || newCoordinatesRef.current;
                return `
                  <p style="margin: 4px 0; color: ${darkMode ? '#52c41a' : '#52c41a'}; font-size: 11px; text-align: center;">
                    Yeni konum: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}
                  </p>
                `;
              })() : ''}
            ` : (!item.offsite && !(item.bkm && item.onsite === 'Offsite')) ? `
              <div style="display: flex; gap: 8px;">
                <button id="startDraggingBtn" 
                        style="flex: 1; display: flex; align-items: center; justify-content: center; background: ${darkMode ? '#faad14' : '#faad14'}; color: #FFFFFF; padding: 6px 12px; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 2px 0 rgba(0, 0, 0, 0.043);">
                  Taşı
                </button>
                <button id="editLocationBtn" 
                        style="flex: 1; display: flex; align-items: center; justify-content: center; background: ${darkMode ? '#E9762B' : '#E9762B'}; color: #FFFFFF; padding: 6px 12px; border: none; border-radius: 4px; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 2px 0 rgba(0, 0, 0, 0.043);">
                  Düzenle
                </button>
              </div>
            ` : ''}
            ${hideDetailLink || item.offsite || (item.bkm && item.onsite === 'Offsite') ? '' : `
              <a href="/${detailLinkPath}/${item.maptriksID || item.id}" target="_blank" 
                 style="display: flex; align-items: center; justify-content: center; background: ${darkMode ? '#177ddc' : '#05111C'}; color: ${darkMode ? '#FFFFFF !important' : '#FFFFFF !important'}; padding: 4px 12px; text-decoration: none; border-radius: 4px; font-size: 12px; font-weight: 500; transition: all 0.3s ease; border: 1px solid ${darkMode ? '#177ddc' : '#1890ff'}; box-shadow: 0 2px 0 rgba(0, 0, 0, 0.043);">
                Detayları Görüntüle
              </a>
            `}
          </div>
        </div>
      `);
  
      setTimeout(() => {
        if (infoWindowRef.current && mapInstance.current) {
          // Eğer aynı marker için zaten açıksa, sadece içeriği güncelle, yeniden açma
          const currentAnchor = infoWindowRef.current.getAnchor ? infoWindowRef.current.getAnchor() : null;
          if (isSameMarker && currentAnchor === marker) {
            // Zaten açık, sadece içerik güncellendi
          } else {
            infoWindowRef.current.open({
              anchor: marker,
              map: mapInstance.current,
              shouldFocus: false,
            });
          }
        }
        
        setTimeout(() => {
          const startDraggingBtn = document.getElementById('startDraggingBtn');
          const saveLocationBtn = document.getElementById('saveLocationBtn');
          const cancelDraggingBtn = document.getElementById('cancelDraggingBtn');
          const editLocationBtn = document.getElementById('editLocationBtn');
          
          // Eğer aynı marker için infoWindow zaten açıksa ve event listener'lar zaten eklenmişse, tekrar ekleme
          const markerId = String(item.maptriksID || item.id);
          const existingListenersKey = `_listeners_${markerId}`;
          
          if (startDraggingBtn && !startDraggingBtn[existingListenersKey]) {
            const oldHandler = startDraggingBtn._startDraggingHandler;
            if (oldHandler) {
              startDraggingBtn.removeEventListener('click', oldHandler);
            }
            
            const clickHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (handleStartDraggingRef.current) {
                handleStartDraggingRef.current(marker, item);
              } else {
                console.error('handleStartDraggingRef.current bulunamadı');
              }
            };
            
            startDraggingBtn._startDraggingHandler = clickHandler;
            startDraggingBtn.addEventListener('click', clickHandler, { once: false });
            startDraggingBtn[existingListenersKey] = true;
          }
          
          if (editLocationBtn && !editLocationBtn[existingListenersKey]) {
            const oldHandler = editLocationBtn._editHandler;
            if (oldHandler) {
              editLocationBtn.removeEventListener('click', oldHandler);
            }
            
            const clickHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedMarkerData({
                id: item.maptriksID || item.id,
                enlem: item.coordinates?.lat || item.Enlem,
                boylam: item.coordinates?.lng || item.Boylam,
                adres: item.address || item.Adres || '',
                maptriksID: item.maptriksID || item.id,
                tableName: 'maptriks_lokasyonlar',
                uniqueField: 'MaptriksID',
                uniqueValue: item.maptriksID || item.id
              });
              setIsFormModalVisible(true);
            };
            
            editLocationBtn._editHandler = clickHandler;
            editLocationBtn.addEventListener('click', clickHandler, { once: false });
            editLocationBtn[existingListenersKey] = true;
          }
          
          if (saveLocationBtn) {
            // Her zaman önceki handler'ı kaldır (çünkü dragend'de tekrar çağrılabilir)
            const oldHandler = saveLocationBtn._saveHandler;
            if (oldHandler) {
              saveLocationBtn.removeEventListener('click', oldHandler, true);
            }
            
            const clickHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation(); // Diğer listener'ları da durdur
              
              // Eğer zaten kaydetme işlemi devam ediyorsa, yeni isteği yok say
              if (isSavingLocationRef.current) {
                console.log('Save operation already in progress, ignoring click...');
                return;
              }
              
              // Butonu hemen devre dışı bırak
              if (saveLocationBtn) {
                saveLocationBtn.disabled = true;
                saveLocationBtn.style.pointerEvents = 'none';
                saveLocationBtn.style.opacity = '0.6';
              }
              
              if (handleSaveLocationRef.current) {
                handleSaveLocationRef.current(marker, item).finally(() => {
                  // İşlem bittiğinde butonu tekrar etkinleştir
                  setTimeout(() => {
                    if (saveLocationBtn) {
                      saveLocationBtn.disabled = false;
                      saveLocationBtn.style.pointerEvents = 'auto';
                      saveLocationBtn.style.opacity = '1';
                    }
                  }, 500);
                });
              }
            };
            
            saveLocationBtn._saveHandler = clickHandler;
            saveLocationBtn.addEventListener('click', clickHandler, { once: false, capture: true });
          }
          
          if (cancelDraggingBtn) {
            // Her zaman önceki handler'ı kaldır (çünkü dragend'de tekrar çağrılabilir)
            const oldHandler = cancelDraggingBtn._cancelHandler;
            if (oldHandler) {
              cancelDraggingBtn.removeEventListener('click', oldHandler);
            }
            
            const clickHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation(); // Diğer listener'ları da durdur
              if (handleCancelDraggingRef.current) {
                handleCancelDraggingRef.current(marker, item);
              }
            };
            
            cancelDraggingBtn._cancelHandler = clickHandler;
            cancelDraggingBtn.addEventListener('click', clickHandler, { once: false, capture: true });
          }
        }, 50);
      }, 10);
  
      if (images && images.length > 0) {
        setTimeout(() => {
          const imageContainer = document.getElementById('imageContainer');
          if (imageContainer) {
            imageContainer.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              openImageModal(images, 0, item.maptriksID || item.id, item.lokasyonResim || item.resim);
            });
          }
        }, 100);
      }
    }, [darkMode, hideDetailLink, detailLinkPath, openImageModal, draggingMarkerId, newCoordinates, isSavingLocation]);
  
  const createCustomMarkerIcon = useCallback((item, isSelected = false) => {
    const bankaAdi = item.banka;
    if (bankaAdi) {
      const normalizedBankaAdi = bankaAdi
        .toLowerCase()
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]/g, '')
        .trim();
      
      if (normalizedBankaAdi) {
        const _u = getMarkerImage(bankaAdi, 64, 64, 80);
        const _sz = isSelected ? 40 : 30;
        return { url: _u, scaledSize: new window.google.maps.Size(_sz, _sz), anchor: new window.google.maps.Point(_sz / 2, _sz / 2) };
      }
    }
    if (item.bkm && !item.banka) {
      const size = isSelected ? 24 : 16;
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="#ff6b35" stroke="#fff" stroke-width="2"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold">BKM</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2)
      };
    }
    
    try {
      const markerComponent = (
        <DynamicMarker
          firma={item.firma}
          durum={item.durum}
          durum_name={item.durum_name}
          segment={item.segment}
          resim={item.lokasyonResim}
          size={32}
          isSelected={isSelected}
        />
      );

      const markerHtml = ReactDOMServer.renderToString(markerComponent);

      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32">
            <foreignObject width="32" height="32">
              <div xmlns="http://www.w3.org/1999/xhtml">${markerHtml}</div>
            </foreignObject>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16)
      };
    } catch (error) {
      console.error('Error creating DynamicMarker, using fallback:', error);
      const size = isSelected ? 40 : 32;
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="#1890ff" stroke="#fff" stroke-width="2"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-size="10" font-weight="bold">LOC</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2)
      };
    }
  }, []);

  const handleStartDraggingRef = useRef(null);
  const handleSaveLocationRef = useRef(null);
  const handleCancelDraggingRef = useRef(null);

  const handleStartDragging = useCallback(async (marker, item) => {
    Object.keys(markersRef.current).forEach(id => {
      const existingMarker = markersRef.current[id];
      if (existingMarker && existingMarker._isDraggingMarker) {
        if (existingMarker.dragListener) {
          window.google.maps.event.removeListener(existingMarker.dragListener);
        }
        if (existingMarker.dragStartListener) {
          window.google.maps.event.removeListener(existingMarker.dragStartListener);
        }
        if (existingMarker.dragEndListener) {
          window.google.maps.event.removeListener(existingMarker.dragEndListener);
        }
        if (existingMarker.mousedownListener) {
          window.google.maps.event.removeListener(existingMarker.mousedownListener);
        }
        if (existingMarker.clickListenerForDrag) {
          window.google.maps.event.removeListener(existingMarker.clickListenerForDrag);
        }
        existingMarker.setMap(null);
        delete markersRef.current[id];
      }
    });
    
    setDraggingMarkerId(null);
    setNewCoordinates(null);
    newCoordinatesRef.current = null;
    
    const markerId = String(item.maptriksID || item.id);
    let actualMarker = markersRef.current[markerId];
    let currentPos;
    currentPos = actualMarker.getPosition();

    originalCoordinatesRef.current = {
      lat: currentPos.lat(),
      lng: currentPos.lng()
    };
    console.log("originalCoordinatesRef.current", originalCoordinatesRef.current);

    setDraggingMarkerId(markerId);
    setNewCoordinates(null);
    newCoordinatesRef.current = null;

    if (actualMarker.clickListener) {
      console.log("actualMarker.clickListener", actualMarker.clickListener);
      window.google.maps.event.removeListener(actualMarker.clickListener);
      actualMarker.clickListener = null;
    }

    if (actualMarker.dragListener) {
      console.log("actualMarker.dragListener", actualMarker.dragListener);
      window.google.maps.event.removeListener(actualMarker.dragListener);
      actualMarker.dragListener = null;
    }

    const currentPosition = actualMarker.getPosition();
    const currentIcon = actualMarker.getIcon();
    const currentTitle = actualMarker.getTitle();

    const originalMarkerData = {
      icon: currentIcon,
      title: currentTitle,
      position: currentPosition
    };
    console.log("originalMarkerData", originalMarkerData);

    actualMarker.setMap(null);
    actualMarker.setVisible(false);

    if (!window.google || !window.google.maps || !window.google.maps.Marker) {
      console.error('Google Maps API yüklenmemiş!');
      message.error('Google Maps yüklenemedi, lütfen sayfayı yenileyin');
      return;
    }
    
    const draggableMarker = new window.google.maps.Marker({
      position: currentPosition,
      map: mapInstance.current,
      title: 'Sürükleyerek taşıyın',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 15,
        fillColor: '#faad14',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 3
      },
      draggable: true,
      clickable: true,
      optimized: false,
      zIndex: 10000,
      animation: null,
      crossOnDrag: false
    });
    setTimeout(() => {
      draggableMarker.setMap(null);
      setTimeout(() => {
        draggableMarker.setMap(mapInstance.current);
        draggableMarker.setDraggable(true);
        draggableMarker.setClickable(true);
        
        const currentPos = draggableMarker.getPosition();
        if (currentPos) {
          const newPos = new window.google.maps.LatLng(
            currentPos.lat() + 0.0000001,
            currentPos.lng() + 0.0000001
          );
          draggableMarker.setPosition(newPos);
          setTimeout(() => {
            draggableMarker.setPosition(currentPosition);
            draggableMarker.setDraggable(true);
            draggableMarker.setClickable(true);
            console.log('Marker uyandırıldı ve draggable ayarlandı:', {
              draggable: draggableMarker.getDraggable(),
              clickable: draggableMarker.getClickable(),
              map: draggableMarker.getMap() !== null
            });
          }, 10);
        }
      }, 50);
    }, 100);

    draggableMarker._isDraggingMarker = true;
    draggableMarker._originalMarkerData = originalMarkerData;
    draggableMarker._item = item;

    markersRef.current[markerId] = draggableMarker;
    const hasMap = draggableMarker.getMap() !== null;        
    const isDraggableNow = draggableMarker.getDraggable();
    const isClickableNow = draggableMarker.getClickable();
    
    console.log('Sürüklenebilir marker oluşturuldu:', {
      markerId,
      position: currentPosition,
      draggable: isDraggableNow,
      clickable: isClickableNow,
      map: hasMap
    });

    const mousedownListener = draggableMarker.addListener('mousedown', (e) => {
      if (e && e.domEvent) {
        e.domEvent.stopPropagation();
        e.domEvent.preventDefault();
      }
    });

    const clickListener = draggableMarker.addListener('click', (e) => {
      console.log('Marker click - event durduruldu');
      if (e && e.domEvent) {
        e.domEvent.stopPropagation();
      }
      e.stop();
    });

    const dragStartListener = draggableMarker.addListener('dragstart', () => {
      console.log('Marker sürüklenmeye başladı');
    });

    const dragListener = draggableMarker.addListener('drag', () => {
      try {
        const newPos = draggableMarker.getPosition();
        const newCoords = {
          lat: newPos.lat(),
          lng: newPos.lng()
        };
        newCoordinatesRef.current = newCoords;
      } catch (e) {
        console.error('Drag listener hatası:', e);
      }
    });

    const dragEndListener = draggableMarker.addListener('dragend', () => {
      try {
        const newPos = draggableMarker.getPosition();
        const newCoords = {
          lat: newPos.lat(),
          lng: newPos.lng()
        };
        console.log('Marker dragged to:', newCoords);
        // Drag bittiğinde state'i güncelle
        setNewCoordinates(newCoords);
        newCoordinatesRef.current = newCoords;
        
        const updatedItem = {
          ...item,
          coordinates: newCoords
        };
        // InfoWindow'u güncelle ama yeniden açma (zaten açıksa)
        showInfoWindow(draggableMarker, updatedItem, newCoords);
      } catch (e) {
        console.error('Drag end listener hatası:', e);
      }
    });

    draggableMarker.mousedownListener = mousedownListener;
    draggableMarker.clickListenerForDrag = clickListener;
    draggableMarker.dragStartListener = dragStartListener;
    draggableMarker.dragListener = dragListener;
    draggableMarker.dragEndListener = dragEndListener;
    draggableMarker.clickListener = null;
    message.info('Marker\'ı yeni konuma sürükleyin, ardından "Kaydet" veya "İptal" butonuna tıklayın');
  }, [mapInstance, markersRef, infoWindowRef, showInfoWindow, setDraggingMarkerId, setNewCoordinates, originalCoordinatesRef]);

  const handleSaveLocation = useCallback(async (marker, item) => {
    // Eğer zaten kaydetme işlemi devam ediyorsa, yeni isteği yok say
    if (isSavingLocationRef.current) {
      console.log('Save operation already in progress, ignoring...');
      return;
    }
    
    isSavingLocationRef.current = true;
    setIsSavingLocation(true);
    
    // Önce ref'ten kontrol et (drag sırasında güncellenen), sonra state'ten, son olarak marker'dan
    let coordsToSave = newCoordinatesRef.current || newCoordinates;
    console.log('handleSaveLocation - coordsToSave (ref/state):', coordsToSave);
    
    // Eğer hala koordinat yoksa, marker'dan al
    if (!coordsToSave && marker) {
      try {
        const currentPos = marker.getPosition();
        if (currentPos) {
          coordsToSave = {
            lat: currentPos.lat(),
            lng: currentPos.lng()
          };
          console.log('handleSaveLocation - coordsToSave (marker):', coordsToSave);
        }
      } catch (e) {
        console.error('Marker pozisyonu alınırken hata:', e);
      }
    }
    
    if (!coordsToSave) {
      console.error('Koordinat bulunamadı - newCoordinatesRef:', newCoordinatesRef.current, 'newCoordinates:', newCoordinates, 'marker:', marker);
      isSavingLocationRef.current = false;
      setIsSavingLocation(false);
      return;
    }
    
    console.log('handleSaveLocation - Final coordsToSave:', coordsToSave);

    try {
      const maptriksId = item.maptriksID || item.id;
      await axios.put(`${API_BASE_URL}/atm/${maptriksId}`, {
        Enlem: coordsToSave.lat,
        Boylam: coordsToSave.lng,
        'Enlem-Boylam': `${coordsToSave.lat},${coordsToSave.lng}`
      });
      message.success('Lokasyon başarıyla güncellendi');
      const updatedItem = {
        ...item,
        coordinates: {
          lat: coordsToSave.lat,
          lng: coordsToSave.lng
        }
      };

      const originalData = marker && marker._originalMarkerData 
        ? marker._originalMarkerData 
        : { 
            icon: marker ? marker.getIcon() : null, 
            title: item.name 
          };
      
      const newPosition = new window.google.maps.LatLng(
        coordsToSave.lat,
        coordsToSave.lng
      );

      const markerId = String(item.maptriksID || item.id);
      const targetMarker = markersRef.current[markerId];
      if (targetMarker) {
        if (targetMarker.clickListener) {
          window.google.maps.event.removeListener(targetMarker.clickListener);
        }
        if (targetMarker.dragListener) {
          window.google.maps.event.removeListener(targetMarker.dragListener);
        }
        if (targetMarker.dragStartListener) {
          window.google.maps.event.removeListener(targetMarker.dragStartListener);
        }
        if (targetMarker.dragEndListener) {
          window.google.maps.event.removeListener(targetMarker.dragEndListener);
        }
        if (targetMarker.mousedownListener) {
          window.google.maps.event.removeListener(targetMarker.mousedownListener);
        }
        if (targetMarker.clickListenerForDrag) {
          window.google.maps.event.removeListener(targetMarker.clickListenerForDrag);
        }
        
        targetMarker.setVisible(false);
        targetMarker.setMap(null);
        delete markersRef.current[markerId];
      }

      const updatedItemForIcon = {
        ...updatedItem,
        coordinates: coordsToSave
      };
      const markerIcon = createCustomMarkerIcon 
        ? createCustomMarkerIcon(updatedItemForIcon, false)
        : (originalData.icon || null);
      
      const normalMarker = new window.google.maps.Marker({
        position: newPosition,
        map: mapInstance.current,
        title: originalData.title || item.name,
        icon: markerIcon,
        draggable: false,
        optimized: true,
        zIndex: undefined
      });

      const clickListener = normalMarker.addListener('click', (e) => {
        e.stop();
        if (onSelectLocation) {
          onSelectLocation(updatedItem);
        }
        showInfoWindow(normalMarker, updatedItem);
      });
      normalMarker.clickListener = clickListener;
      markersRef.current[markerId] = normalMarker;
      savedCoordinatesRef.current[markerId] = {
        lat: coordsToSave.lat,
        lng: coordsToSave.lng
      };

      setDraggingMarkerId(null);
      setNewCoordinates(null);
      newCoordinatesRef.current = null;
      originalCoordinatesRef.current = null;
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        setTimeout(() => {
          if (infoWindowRef.current) {
            const anchor = infoWindowRef.current.getAnchor ? infoWindowRef.current.getAnchor() : null;
            if (anchor && anchor._isDraggingMarker) {
              infoWindowRef.current.close();
            }
          }
        }, 100);
      }

      if (onSelectLocation) {
        onSelectLocation(updatedItem);
      }
    } catch (error) {
      console.error('Lokasyon güncellenirken hata:', error);
      message.error('Lokasyon güncellenirken hata oluştu');
    } finally {
      isSavingLocationRef.current = false;
      setIsSavingLocation(false);
    }
  }, [newCoordinates, API_BASE_URL, onSelectLocation, showInfoWindow, mapInstance, markersRef, savedCoordinatesRef, setDraggingMarkerId, setNewCoordinates, setIsSavingLocation, originalCoordinatesRef, isSavingLocationRef, createCustomMarkerIcon]);

  const handleCancelDragging = useCallback((marker, item) => {
    if (!mapInstance.current) return;

    let originalPos;
    if (originalCoordinatesRef.current) {
      originalPos = new window.google.maps.LatLng(
        originalCoordinatesRef.current.lat,
        originalCoordinatesRef.current.lng
      );
    } else if (item.coordinates) {
      originalPos = new window.google.maps.LatLng(item.coordinates.lat, item.coordinates.lng);
    } else {
      console.warn('Orijinal pozisyon bulunamadı');
      return;
    }

    const originalData = marker && marker._originalMarkerData 
      ? marker._originalMarkerData 
      : { 
          icon: marker ? marker.getIcon() : null, 
          title: item.name 
        };

    const markerId = String(item.maptriksID || item.id);
    
    Object.keys(markersRef.current).forEach(id => {
      const existingMarker = markersRef.current[id];
      if (existingMarker && existingMarker._isDraggingMarker) {
        if (existingMarker.clickListener) {
          window.google.maps.event.removeListener(existingMarker.clickListener);
        }
        if (existingMarker.dragListener) {
          window.google.maps.event.removeListener(existingMarker.dragListener);
        }
        if (existingMarker.dragStartListener) {
          window.google.maps.event.removeListener(existingMarker.dragStartListener);
        }
        if (existingMarker.dragEndListener) {
          window.google.maps.event.removeListener(existingMarker.dragEndListener);
        }
        if (existingMarker.mousedownListener) {
          window.google.maps.event.removeListener(existingMarker.mousedownListener);
        }
        if (existingMarker.clickListenerForDrag) {
          window.google.maps.event.removeListener(existingMarker.clickListenerForDrag);
        }
        existingMarker.setVisible(false);
        existingMarker.setMap(null);
        delete markersRef.current[id];
      }
    });
    
    const markerIcon = createCustomMarkerIcon 
      ? createCustomMarkerIcon(item, false)
      : (originalData.icon || null);
    
    const normalMarker = new window.google.maps.Marker({
      position: originalPos,
      map: mapInstance.current,
      title: originalData.title || item.name,
      icon: markerIcon,
      draggable: false,
      optimized: true,
      zIndex: undefined
    });

    const clickListener = normalMarker.addListener('click', (e) => {
      e.stop();
      if (onSelectLocation) {
        onSelectLocation(item);
      }
      showInfoWindow(normalMarker, item);
    });
    normalMarker.clickListener = clickListener;
    markersRef.current[markerId] = normalMarker;
    
    if (savedCoordinatesRef.current[markerId]) {
      delete savedCoordinatesRef.current[markerId];
    }
    
    setDraggingMarkerId(null);
    setNewCoordinates(null);
    newCoordinatesRef.current = null;
    originalCoordinatesRef.current = null;

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, [showInfoWindow, onSelectLocation, mapInstance, markersRef, setDraggingMarkerId, setNewCoordinates, originalCoordinatesRef, savedCoordinatesRef, infoWindowRef, createCustomMarkerIcon]);

  useEffect(() => {
    handleStartDraggingRef.current = handleStartDragging;
    handleSaveLocationRef.current = handleSaveLocation;
    handleCancelDraggingRef.current = handleCancelDragging;
  }, [handleStartDragging, handleSaveLocation, handleCancelDragging]);

  useImperativeHandle(ref, () => ({
    panTo: (coordinates) => {
      if (mapInstance.current) {
        mapInstance.current.panTo(coordinates);
      }
    },
    setZoom: (zoom) => {
      if (mapInstance.current) {
        mapInstance.current.setZoom(zoom);
      }
    },
    getCurrentZoom: () => {
      if (mapInstance.current) {
        return mapInstance.current.getZoom();
      }
      return 10;
    },
    openInfoWindow: (item) => {
      if (mapInstance.current && item) {        
        mapInstance.current.panTo(item.coordinates);
        mapInstance.current.setZoom(17);
        const marker = markersRef.current[String(item.id)];
        if (marker) {
          showInfoWindow(marker, item);
        } else {
          let attempts = 0;
          const maxAttempts = 10;
          
          const tryOpenInfoWindow = () => {
            attempts++;
            const delayedMarker = markersRef.current[String(item.id)];            
            if (delayedMarker) {
              showInfoWindow(delayedMarker, item);
            } else if (attempts < maxAttempts) {
              setTimeout(tryOpenInfoWindow, 200);
            } else {
              console.warn('Could not find marker after', maxAttempts, 'attempts');
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
              }
              if (!infoWindowRef.current) {
                infoWindowRef.current = new window.google.maps.InfoWindow();
              }
              
              const tempMarker = new window.google.maps.Marker({
                position: item.coordinates,
                map: null, 
                title: item.name
              });
              
              showInfoWindow(tempMarker, item);
            }
          };
          setTimeout(tryOpenInfoWindow, 100);
        }
      }
    }
  }));

  useEffect(() => {
    const initMap = () => {
      infoWindowRef.current = new window.google.maps.InfoWindow();
      setMapLoaded(true);
    };

    if (!window.google || !window.google.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&callback=initMap`;
      script.async = true;
      script.defer = true;
      window.initMap = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }

    return () => {
      window.initMap = null;
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
    };
  }, []);

  // Harita init effect'i data/filtre değişiminde yeniden çalışıp haritayı YENİDEN
  // yaratmasın diye filtre callback'leri ve showBkmAtms ref'te tutulur. Aksi halde
  // yeniden filtreleme (data değişimi) init effect'i tetikleyip yeni bir map yaratıyor
  // ve tüm markerlar kayboluyordu (harita marker kaybolma bug fix).
  const filterBkmRef = useRef(filterBkmAtmsByViewport);
  const filterNormalRef = useRef(filterNormalLocationsByViewport);
  const showBkmAtmsRef = useRef(showBkmAtms);
  useEffect(() => { filterBkmRef.current = filterBkmAtmsByViewport; }, [filterBkmAtmsByViewport]);
  useEffect(() => { filterNormalRef.current = filterNormalLocationsByViewport; }, [filterNormalLocationsByViewport]);
  useEffect(() => { showBkmAtmsRef.current = showBkmAtms; }, [showBkmAtms]);

  useEffect(() => {
    if (!mapLoaded) return;

    const initializeMap = () => {
      if (mapInstance.current) return; // harita yalnızca bir kez yaratılır (re-filter'da yeniden yaratma)
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 39.9334, lng: 32.8597 },
        zoom: 8,
        styles: darkMode ? darkMapStyles : lightMapStyles,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: ['roadmap', 'satellite']
        },
        streetViewControl: true,
        fullscreenControl: true,
        gestureHandling: 'greedy'
      });

      mapInstance.current.addListener('click', (event) => {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        
        if (isAddMarkerModeRef.current) return;
        if (isMeasuringDistanceRef.current && isFirstPointSelectedRef.current) {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          measureDistance(lat, lng, false); 
        }
      });

      mapInstance.current.addListener('rightclick', (event) => {
        event.stop();
        if (isAddMarkerModeRef.current) return; 
        
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        measureDistance(lat, lng, true); 
      });

      const debouncedBoundsUpdate = debounce(() => {
        if (!mapInstance.current) return;
        
        const bounds = mapInstance.current.getBounds();
        const zoom = mapInstance.current.getZoom();
        
        setMapBounds(prevBounds => {
          if (!prevBounds || !bounds.equals(prevBounds)) {
            setCurrentZoom(zoom);
            
            const filteredNormal = filterNormalRef.current(bounds);
            setFilteredNormalData(filteredNormal);

       if (showBkmAtmsRef.current) {
         if (zoom >= 14) {
           const filteredBkm = filterBkmRef.current(bounds, zoom);
           setFilteredBkmData(filteredBkm);
         } else {
           setFilteredBkmData([]);
         }
       } else {
         setFilteredBkmData([]);
       }
            return bounds;
          }
          return prevBounds;
        });
      }, 800); 
      mapInstance.current.addListener('idle', debouncedBoundsUpdate);

      if (mapInstance.current) {
        const initialBounds = mapInstance.current.getBounds();
        const initialZoom = mapInstance.current.getZoom();
        if (initialBounds) {
          setMapBounds(initialBounds);
          setCurrentZoom(initialZoom);
          
          const initialFilteredNormal = filterNormalRef.current(initialBounds);
          setFilteredNormalData(initialFilteredNormal);

           if (showBkmAtmsRef.current && initialZoom >= 14) {
             const initialFilteredBkm = filterBkmRef.current(initialBounds, initialZoom);
             setFilteredBkmData(initialFilteredBkm);
           } else {
             setFilteredBkmData([]);
           }
        } else {
          setCurrentZoom(initialZoom);
        }
      }

      if (window.google && window.google.maps && window.google.maps.TransitLayer) {
        transitLayerRef.current = new window.google.maps.TransitLayer();
        transitLayerRef.current.setMap(mapInstance.current);
      }
    };

    initializeMap();

  }, [mapLoaded]); // yalnızca mapLoaded'da; darkMode ayrı effect'te setOptions ile, filtreler ref ile

  useEffect(() => {
    if (!mapInstance.current) return;

    const handleMapClick = (event) => {
      if (isAddMarkerModeRef.current) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        addCustomMarker(lat, lng);
        setIsAddMarkerMode(false);
        isAddMarkerModeRef.current = false; 
      }
    };

    const listener = mapInstance.current.addListener('click', handleMapClick);

    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [addCustomMarker]);

  useEffect(() => {
    if (!mapLoaded || !mapInstance.current || !data || data.length === 0) return;

    const bounds = calculateBoundsFromData(data);
    if (bounds) {
      const googleBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.south, bounds.west),
        new window.google.maps.LatLng(bounds.north, bounds.east)
      );
      
      const padding = 0.01; 
      googleBounds.extend(new window.google.maps.LatLng(bounds.south - padding, bounds.west - padding));
      googleBounds.extend(new window.google.maps.LatLng(bounds.north + padding, bounds.east + padding));
      
      mapInstance.current.fitBounds(googleBounds);
    } else {
      const center = calculateCenterFromData(data);
      mapInstance.current.setCenter(center);
      mapInstance.current.setZoom(10);
    }
  }, [mapLoaded, data]);

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setMapTypeId('roadmap');
      setTimeout(() => {
        if (mapInstance.current) {
          mapInstance.current.setOptions({ 
            styles: darkMode ? darkMapStyles : lightMapStyles,
            mapTypeControlOptions: {
              mapTypeIds: ['roadmap', 'satellite']
            }
          });
          if (!transitLayerRef.current && window.google && window.google.maps && window.google.maps.TransitLayer) {
            transitLayerRef.current = new window.google.maps.TransitLayer();
            transitLayerRef.current.setMap(mapInstance.current);
          }
        }
      }, 100);
    }
  }, [darkMode]);

  useEffect(() => {
    if (distanceMarkersRef.current.length > 0) {
      updateDistanceLines();
    }
  }, [darkMode, updateDistanceLines]);

  useEffect(() => {
    if (!mapLoaded || !mapInstance.current || !selectedItemId) return;
    const idx = data.findIndex(item => item && String(item.id) === String(selectedItemId));
    if (idx !== -1 && data[idx] && mapInstance.current) {
      const selected = data[idx];
      mapInstance.current.panTo(selected.coordinates);
      mapInstance.current.setZoom(17);
      setSelectedMarkerIndex(idx);
      const marker = markersRef.current[String(selected.id)];
      if (marker) {
        showInfoWindow(marker, selected);
      }
    }
  }, [selectedItemId, mapLoaded, data, showInfoWindow]);

  useEffect(() => {
    if (!mapLoaded || !mapInstance.current) return;
    
    const currentZoom = mapInstance.current.getZoom();
    const existingMarkerIds = new Set(Object.keys(markersRef.current));
    const visibleData = [...data, ...(showBkmAtms ? offsiteData : [])];
    
    const newMarkerIds = new Set(visibleData.map(item => String(item.id)));
    
    existingMarkerIds.forEach(id => {
      if (!newMarkerIds.has(id)) {
        if (markersRef.current[id]) {
          if (markersRef.current[id].clickListener) {
            window.google.maps.event.removeListener(markersRef.current[id].clickListener);
          }
          if (markersRef.current[id].dragListener) {
            window.google.maps.event.removeListener(markersRef.current[id].dragListener);
          }
          markersRef.current[id].setMap(null);
          delete markersRef.current[id];
        }
      }
    });
    
    visibleData.forEach((item, index) => {
      const markerId = String(item.maptriksID || item.id);
      const isSelected = String(item.maptriksID || item.id) === String(selectedItemId);
      const existingMarker = markersRef.current[markerId];
      const savedCoords = savedCoordinatesRef.current[markerId];
      const targetCoords = savedCoords || item.coordinates;
      const targetPosition = new window.google.maps.LatLng(targetCoords.lat, targetCoords.lng);
      if (savedCoords) {
        Object.keys(markersRef.current).forEach(id => {
          if (id === markerId) {
            const marker = markersRef.current[id];
            if (marker && !marker.getDraggable()) {
              const markerPos = marker.getPosition();
              if (Math.abs(markerPos.lat() - savedCoords.lat) > 0.000001 || 
                  Math.abs(markerPos.lng() - savedCoords.lng) > 0.000001) {
                if (marker.clickListener) {
                  window.google.maps.event.removeListener(marker.clickListener);
                }
                if (marker.dragListener) {
                  window.google.maps.event.removeListener(marker.dragListener);
                }
                marker.setMap(null);
                delete markersRef.current[id];
              }
            }
          }
        });
      }
      
      if (existingMarker && !existingMarker.getDraggable()) {
        const currentPos = existingMarker.getPosition();
        
        if (Math.abs(currentPos.lat() - targetPosition.lat()) > 0.000001 || 
            Math.abs(currentPos.lng() - targetPosition.lng()) > 0.000001) {
          existingMarker.setPosition(targetPosition);
        }
        const newIcon = createCustomMarkerIcon(item, isSelected);
        existingMarker.setIcon(newIcon);
        existingMarker.setTitle(item.name);
        return;
      }
      
      const isCurrentlyDragging = draggingMarkerId === markerId;
      if (!existingMarker || existingMarker.getDraggable()) {
        if (existingMarker) {
          if (isCurrentlyDragging) {
            return; 
          }
          
          const isDraggable = existingMarker.getDraggable ? existingMarker.getDraggable() : false;
          const icon = existingMarker.getIcon ? existingMarker.getIcon() : null;
          const isYellowMarker = existingMarker._isDraggingMarker === true || 
            (isDraggable === true && icon && typeof icon === 'object' && icon.fillColor === '#faad14');
          
          if (isDraggable || isYellowMarker) {
            if (existingMarker.clickListener) {
              window.google.maps.event.removeListener(existingMarker.clickListener);
            }
            if (existingMarker.dragListener) {
              window.google.maps.event.removeListener(existingMarker.dragListener);
            }
            if (existingMarker.dragStartListener) {
              window.google.maps.event.removeListener(existingMarker.dragStartListener);
            }
            if (existingMarker.dragEndListener) {
              window.google.maps.event.removeListener(existingMarker.dragEndListener);
            }
            if (existingMarker.mousedownListener) {
              window.google.maps.event.removeListener(existingMarker.mousedownListener);
            }
            if (existingMarker.clickListenerForDrag) {
              window.google.maps.event.removeListener(existingMarker.clickListenerForDrag);
            }
            existingMarker.setVisible(false);
            existingMarker.setMap(null);
            delete markersRef.current[markerId];
          } else {
            if (existingMarker.clickListener) {
              window.google.maps.event.removeListener(existingMarker.clickListener);
            }
            if (existingMarker.dragListener) {
              window.google.maps.event.removeListener(existingMarker.dragListener);
            }
            existingMarker.setMap(null);
          }
        }
        
        if ((!existingMarker || !existingMarker.getDraggable()) && !isCurrentlyDragging) {
          const marker = new window.google.maps.Marker({
            position: targetPosition, 
            map: mapInstance.current,
            title: item.name,
            icon: createCustomMarkerIcon(item, isSelected),
            optimized: true
          });

          const clickListener = marker.addListener('click', (e) => {
            if (marker.getDraggable()) {
              e.stop(); 
              return;
            }
            e.stop();
            if (onSelectLocation) {
              onSelectLocation(item);
            }
            showInfoWindow(marker, item);
          });
          marker.clickListener = clickListener;
          markersRef.current[markerId] = marker;
        }
      }
    });
  }, [data, mapLoaded, selectedItemId, onSelectLocation, showInfoWindow, showBkmAtms, currentZoom, offsiteData]);

  const toggleMetroInfo = () => {
    setShowMetroInfo(!showMetroInfo);
  };

  const initMarkerClusterer = useCallback((map) => {
    if (window.MarkerClusterer) {
      markerClusterer.current = new window.MarkerClusterer(map, [], {
        imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
        maxZoom: 15,
        gridSize: 60
      });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (markerClusterer.current) {
        markerClusterer.current.clearMarkers();
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      if (transitLayerRef.current) {
        transitLayerRef.current.setMap(null);
        transitLayerRef.current = null;
      }
      clearDistanceMeasurement();
      Object.keys(markersRef.current).forEach(key => {
        if (key.startsWith('custom-') && markersRef.current[key]) {
          markersRef.current[key].setMap(null);
          delete markersRef.current[key];
        }
      });
    };
  }, [clearDistanceMeasurement]);

  useEffect(() => {
    if (!mapLoaded || !window.google || !window.google.maps.places) return;
    if (mapInstance.current && !markerClusterer.current) {
      initMarkerClusterer(mapInstance.current);
    }
    const moveListener = window.google.maps.event.addListener(
      mapInstance.current,
      'idle',
      debouncedUpdateMarkers
    );

    return () => {
      if (moveListener) {
        window.google.maps.event.removeListener(moveListener);
      }
    };
  }, [mapLoaded, debouncedUpdateMarkers, initMarkerClusterer]);

  useEffect(() => {
    if (!mapLoaded || !window.google || !window.google.maps.places) return;
    const input = document.getElementById('map-search-input');
    if (!input) return;
    if (mapInstance.current && !markerClusterer.current) {
      initMarkerClusterer(mapInstance.current);
    }
    autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
      types: ['geocode', 'establishment'],
    });
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place.geometry) {
        return;
      }

      if (place.geometry.viewport) {
        mapInstance.current.fitBounds(place.geometry.viewport);
      } else {
        mapInstance.current.setCenter(place.geometry.location);
        mapInstance.current.setZoom(17);
      }
    });

    searchBoxRef.current = new window.google.maps.places.SearchBox(input);
    searchBoxRef.current.addListener('places_changed', () => {
      const places = searchBoxRef.current.getPlaces();
      if (places.length === 0) return;

      const place = places[0];
      if (place.geometry.viewport) {
        mapInstance.current.fitBounds(place.geometry.viewport);
      } else {
        mapInstance.current.setCenter(place.geometry.location);
        mapInstance.current.setZoom(17);
      }
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      if (searchBoxRef.current) {
        window.google.maps.event.clearInstanceListeners(searchBoxRef.current);
      }
    };
  }, [mapLoaded, initMarkerClusterer]);

  const renderMapTools = () => {
    const buttonSize = isMobile ? 36 : 30;
    const buttonFontSize = isMobile ? 14 : 16;
    const searchWidth = isMobile ? 180 : 250;
    
    return (
      <div className="map-tools">
        {/* Arama kutusu - en üstte */}
        {!isMobile && (
          <div
            className="map-search-container"
            style={{
              position: 'absolute',
              top: '40px',
              right: '1px',
              zIndex: 1000
            }}
          >
            <Input
              id="map-search-input"
              placeholder="Lokasyon arayın..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: searchWidth,
                borderRadius: 10,
                boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                border: '1px solid #d9d9d9',
                backgroundColor: 'white'
              }}
            />
          </div>
        )}

        {/* Tool butonları - sağda */}
        <div
          className="map-tool-buttons"
          style={{
            position: 'absolute',
            justifyContent: 'flex-end',
            top: isMobile ? '10px' : '80px',
            right: isMobile ? '10px' : '1px',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '8px' : '6px',
            zIndex: 1000
          }}
        >
          {/* Metro info butonu*/}
          {!isMobile && (
            <div
              className="map-tool-button"
              onClick={toggleMetroInfo}
              style={{
                width: `${buttonSize}px`,
                height: `${buttonSize}px`,
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                border: '1px solid #e8e8e8',
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#1890ff',
                transition: 'all 0.3s ease'
              }}
            >
              <span>M</span>
            </div>
          )}

          {/* Konumum butonu */}
          <div
            className="map-tool-button"
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                  const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                  };
                  mapInstance.current.setCenter(pos);
                  mapInstance.current.setZoom(15);
                });
              }
            }}
            style={{
              width: `${buttonSize}px`,
              height: `${buttonSize}px`,
              backgroundColor: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              border: '1px solid #e8e8e8',
              fontSize: `${buttonFontSize}px`,
              color: '#666',
              transition: 'all 0.3s ease'
            }}
          >
            📍
          </div>

          {/* Talep Ekle butonu */}
          <div
            className="map-tool-button"
            style={{
              width: `${buttonSize}px`,
              height: `${buttonSize}px`,
              backgroundColor: isAddMarkerMode ? '#ff4d4f' : 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              border: isAddMarkerMode ? '2px solid #ff7875' : '1px solid #e8e8e8',
              fontSize: `${buttonFontSize}px`,
              color: isAddMarkerMode ? 'white' : '#666',
              transition: 'all 0.3s ease'
            }}
            onClick={() => {
              const newValue = !isAddMarkerMode;
              setIsAddMarkerMode(newValue);
              isAddMarkerModeRef.current = newValue;
              if (!newValue) {
                clearDistanceMeasurement();
              }
            }}
            title={isAddMarkerMode ? 'Marker ekleme modundan çık' : 'Marker ekle'}
          >
            ✜
          </div>

          {/* Mesafe ölçme iptal butonu*/}
          {(isMeasuringDistance || firstPoint || distanceMarkersRef.current.length > 0) && (
            <div
              className="map-tool-button"
              style={{
                width: `${buttonSize}px`,
                height: `${buttonSize}px`,
                backgroundColor: '#ff4d4f',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                border: '2px solid #ff7875',
                fontSize: `${buttonFontSize}px`,
                color: 'white',
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                clearDistanceMeasurement();
              }}
              title="Mesafe ölçmeyi iptal et"
            >
              ✕
            </div>
          )}
        </div>
      </div>
    );
  }; 

  const loadingSpinner = (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    </div>
  );

  return (
    <div className="map-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!mapLoaded ? loadingSpinner : (
        <>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }}></div>
          {renderMapTools()}
          {showMetroInfo && <MetroInfoPanel darkMode={darkMode} isMobile={isMobile} />}
          {isAddMarkerMode && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 77, 79, 0.9)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                zIndex: 1001,
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                pointerEvents: 'none'
              }}
            >
              Haritada bir yere tıklayarak marker ekleyin
            </div>
          )}
          {isMeasuringDistance && distanceMarkersRef.current.length === 1 && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 3, 5, 0.9)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '10px',
                fontWeight: 'lighter',
                zIndex: 1001,
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                pointerEvents: 'none'
              }}
            >
              İkinci noktayı seçmek için haritaya sol tıklayın
            </div>
          )}

          {/* Form Modal */}
          <Modal
            open={isFormModalVisible}
            onCancel={() => {
              setIsFormModalVisible(false);
              setSelectedMarkerData(null);
            }}
            footer={null}
            width={800}
            style={{ top: 20 }}
            destroyOnHidden={true}
          >
            {selectedMarkerData && (
              <div>
                <FormikForm
                  formId={341}
                  uniqueValue={selectedMarkerData.uniqueValue || selectedMarkerData.maptriksID || selectedMarkerData.id}
                  uniqueField={selectedMarkerData.uniqueField || 'MaptriksID'}
                  tableName={selectedMarkerData.tableName || 'maptriks_lokasyonlar'}
                  extraData={{
                    _override_function: 'Update'
                  }}
                  onSuccess={(result) => {
                    setIsFormModalVisible(false);
                    setSelectedMarkerData(null);
                    message.success('Form başarıyla kaydedildi');
                  }}
                />
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  );
});

export default MapContainer;